import { lazy, StrictMode, Suspense, useEffect } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import type { FallbackProps } from 'react-error-boundary';
import { BrowserRouter as Router } from 'react-router-dom';

import FullscreenLoading from '@/shared/components/layouts/views/FullscreenLoading';
import NotionFormModal from '@/shared/components/modals/GlobalModals/NotionFormModal';

import { queryClient } from './clients/tanstack';
import { loadPublicRuntimeConfig } from './config/runtime';
import UrlQueryStateProvider from './providers/00-UrlQueryStateProvider';
import UnauthenticatedProvider from './providers/01-UnauthenticatedProvider';
import { ThemeProvider } from './providers/01-UnauthenticatedProvider/providers';
import { notifyApplicationBootstrapLoaded } from './service-workers/assetRecovery';
import { initServiceWorkerUpdates } from './service-workers/updates';

import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/800.css';
import '@fontsource/inter/900.css';
import '@fontsource/inter/300-italic.css';
import '@fontsource/inter/400-italic.css';
import '@fontsource/inter/500-italic.css';
import '@fontsource/inter/600-italic.css';
import '@fontsource/inter/700-italic.css';
import '@fontsource/inter/800-italic.css';
import '@fontsource/inter/900-italic.css';
import './styles/index.css';

const App = lazy(() => import('./App'));
const OAuthConsentPage = lazy(() => import('./layouts/04-OAuthConsentPage'));
const Shared = lazy(() => import('./layouts/03-Shared'));
const TopLevelErrorBoundary = lazy(() => import('@/shared/components/layouts/views/TopLevelErrorBoundary'));

const ErrorFallback = (props: FallbackProps) => (
    <Suspense fallback={<FullscreenLoading />}>
        <TopLevelErrorBoundary {...props} />
    </Suspense>
);

const ApplicationBootstrapLoaded = () => {
    useEffect(() => notifyApplicationBootstrapLoaded(), []);

    return null;
};

const isSharedLink = location.pathname.includes('/shared/') || location.pathname.includes('/s/');
const isPublicOAuth = location.pathname.includes('/oauth/consent');

// Service Workers
initServiceWorkerUpdates();

const renderApplication = async () => {
    await loadPublicRuntimeConfig();

    const root = document.querySelector('#root');

    if (!root) {
        throw new Error('The application root element is unavailable.');
    }

    ReactDOM.createRoot(root).render(
        <StrictMode>
            <ThemeProvider>
                <QueryClientProvider client={queryClient}>
                    <Router>
                        <UrlQueryStateProvider>
                            <UnauthenticatedProvider>
                                <NotionFormModal />
                                <ErrorBoundary FallbackComponent={ErrorFallback}>
                                    <Suspense fallback={<FullscreenLoading />}>
                                        {isPublicOAuth ? <OAuthConsentPage /> : isSharedLink ? <Shared /> : <App />}
                                        <ApplicationBootstrapLoaded />
                                    </Suspense>
                                </ErrorBoundary>
                            </UnauthenticatedProvider>
                        </UrlQueryStateProvider>
                    </Router>
                </QueryClientProvider>
            </ThemeProvider>
        </StrictMode>,
    );
};

void renderApplication();
